// minisentry/network-patcher.ts
import { getCodeFrame } from './snippet-resolver';
import type { NetworkErrorEvent, SentryStackFrame } from '../types';

/* -------------------------------------------------- */

export const HANDLED = Symbol('__miniSentryHandled');
const INTERNAL_RE = /mini?sentry\/network-patcher/; // frames to skip
const SNIPPET_CTX = 5;                              // ± lines around error
const BODY_LIMIT = 32_768;                          // 32 kB

type Dispatch = (evt: NetworkErrorEvent) => void;

/* -------------------------------------------------- */
/* helpers                                            */
/* -------------------------------------------------- */
function trim(txt: string | null) {
	return txt && txt.length > BODY_LIMIT ? txt.slice(0, BODY_LIMIT) + '…' : txt;
}

function parseFrames(stack: string): SentryStackFrame[] {
	return stack
		.split('\n')
		.map((l) => {
			const m = /at\s+(.*?)\s+\(?(.+):(\d+):(\d+)\)?/.exec(l);
			if (!m) return null;
			return {
				file: m[2],
				lineNumber: Number(m[3]),
				column: Number(m[4]),
				methodName: m[1] || '<anon>',
			} as SentryStackFrame;
		})
		.filter(Boolean) as SentryStackFrame[];
}

async function captureSnippet(stack: string): Promise<string | null> {
	const frames = parseFrames(stack).filter((f) => !INTERNAL_RE.test(f.file));
	if (!frames.length) return null;

	for (const f of frames) {
		try {
			const cf = await getCodeFrame(f, SNIPPET_CTX);
			if (!cf) continue;

			const { context, line } = cf;
			const markerIdx = line - (cf.contextStart ?? line) - 1;

			return context
				.map((ln, idx) => (idx === markerIdx ? `> ${ln}` : `  ${ln}`))
				.join('\n');
		} catch {
			/* keep trying next frame */
		}
	}
	return null;
}

function parseXHRHeaders(raw: string) {
	return raw
		.trim()
		.split(/[\r\n]+/)
		.filter(Boolean)
		.reduce<Record<string, string>>((acc, l) => {
			const i = l.indexOf(':');
			if (i !== -1) acc[l.slice(0, i).trim()] = l.slice(i + 1).trim();
			return acc;
		}, {});
}

/* -------------------------------------------------- */
/* main patch                                         */
/* -------------------------------------------------- */
export function patchNetwork(dispatch: Dispatch) {
	/* --------- fetch --------------------------------------------------- */
	if (typeof window.fetch === 'function') {
		const origFetch = window.fetch.bind(window);
		// @ts-ignore
		window.fetch = async (...args: Parameters<typeof fetch>) => {
			const stackHolder = new Error();          // for snippet

			try {
				const res = await origFetch(...args);
				if (res.ok) return res;

				const snippet = await captureSnippet(stackHolder.stack || '');
				const cloned = res.clone();
				let responseBody: string | null = null;
				try {
					responseBody = trim(await cloned.text());
				} catch {/* ignore */ }

				dispatch({
					type: 'network-error',
					url: res.url,
					method: args[0] instanceof Request ? args[0].method || 'GET' : 'GET',
					status: res.status,
					reason: `${res.status} ${res.statusText}`,
					route: window.location.pathname,
					snippet,
					request: {
						headers:
							args[0] instanceof Request
								? Object.fromEntries(args[0].headers.entries())
								: {},
						body: args[0] instanceof Request ? trim(await args[0].clone().text()) : null,
					},
					response: {
						status: res.status,
						headers: Object.fromEntries(res.headers.entries()),
						body: responseBody,
					},
					buildId: (globalThis as any).__NEXT_DATA__?.buildId ?? 'dev',
					ts: Date.now(),
				});
				return res;
			} catch (err) {
				(err as any)[HANDLED] = true;

				dispatch({
					type: 'network-error',
					url: args[0] instanceof Request ? args[0].url : String(args[0]),
					method: args[0] instanceof Request ? args[0].method || 'GET' : 'GET',
					status: 0,
					reason: (err as Error).message,
					route: window.location.pathname,
					snippet: await captureSnippet(stackHolder.stack || ''),
					request: {
						headers:
							args[0] instanceof Request
								? Object.fromEntries(args[0].headers.entries())
								: {},
						body:
							args[0] instanceof Request
								? trim(await args[0].clone().text())
								: null,
					},
					response: null,
					buildId: (globalThis as any).__NEXT_DATA__?.buildId ?? 'dev',
					ts: Date.now(),
				});
				throw err;
			}
		};
	}

	/* --------- XMLHttpRequest ----------------------------------------- */
	const OrigXHR = window.XMLHttpRequest;
	// @ts-ignore
	window.XMLHttpRequest = function PatchedXHR(this: XMLHttpRequest) {
		const xhr = new OrigXHR();
		let method = 'GET';
		let url = '';
		let reqBody: string | null = null;
		const stackHolder = new Error();

		xhr.open = new Proxy(xhr.open, {
			apply(t, c, args: any) {
				method = args[0];
				url = args[1];
				return Reflect.apply(t, c, args);
			},
		});

		xhr.send = new Proxy(xhr.send, {
			apply(t, c, args: any) {
				reqBody = typeof args[0] === 'string' ? trim(args[0]) : null;
				return Reflect.apply(t, c, args);
			},
		});

		xhr.addEventListener('loadend', async () => {
			if (xhr.status !== 0 && xhr.status < 400) return;

			dispatch({
				type: 'network-error',
				url,
				method,
				status: xhr.status,
				reason: xhr.statusText || 'network error',
				route: window.location.pathname,
				snippet: await captureSnippet(stackHolder.stack || ''),
				request: {
					headers: {},                // cannot read request headers from XHR
					body: reqBody,
				},
				response: {
					status: xhr.status,
					headers: parseXHRHeaders(xhr.getAllResponseHeaders()),
					body: trim(xhr.responseText || null),
				},
				buildId: (globalThis as any).__NEXT_DATA__?.buildId ?? 'dev',
				ts: Date.now(),
			});
		});

		return xhr;
	};
}
